import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { supabase } from '../../supabase/client';

export interface KnowledgePoint {
  id: string;
  name: string;
  category?: string;
}

interface KnowledgePointSelectorProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

const KnowledgePointSelector: React.FC<KnowledgePointSelectorProps> = ({
  selectedIds,
  onChange
}) => {
  const [knowledgePoints, setKnowledgePoints] = useState<KnowledgePoint[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadKnowledgePoints();
  }, []);

  const loadKnowledgePoints = async () => {
    try {
      const { data, error } = await supabase
        .from('knowledge_points')
        .select('id, name, category')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setKnowledgePoints(data || []);
    } catch (error) {
      console.error('加载知识点失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePoint = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(pid => pid !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const filteredPoints = knowledgePoints.filter(kp =>
    kp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (kp.category && kp.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // 按分类分组
  const groupedPoints = filteredPoints.reduce((acc, kp) => {
    const category = kp.category || '其他';
    if (!acc[category]) acc[category] = [];
    acc[category].push(kp);
    return acc;
  }, {} as Record<string, KnowledgePoint[]>);

  const selectedPoints = knowledgePoints.filter(kp => selectedIds.includes(kp.id));

  if (loading) {
    return <div className="text-xs text-gray-400">加载中...</div>;
  }

  return (
    <div className="space-y-3">
      {/* 已选标签 */}
      {selectedPoints.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedPoints.map(kp => (
            <span
              key={kp.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded"
            >
              {kp.name}
              <button
                onClick={() => togglePoint(kp.id)}
                className="hover:text-blue-900"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* 搜索框 */}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="搜索知识点..."
          className="w-full pl-7 pr-3 py-1.5 border border-gray-200 rounded text-xs focus:border-blue-500 focus:outline-none"
        />
      </div>

      {/* 知识点列表 */}
      <div className="max-h-48 overflow-y-auto space-y-2">
        {Object.entries(groupedPoints).map(([category, points]) => (
          <div key={category}>
            <div className="text-xs font-medium text-gray-500 mb-1">{category}</div>
            <div className="flex flex-wrap gap-1">
              {points.map(kp => {
                const isSelected = selectedIds.includes(kp.id);
                return (
                  <button
                    key={kp.id}
                    onClick={() => togglePoint(kp.id)}
                    className={`px-2 py-1 text-xs rounded border transition-colors ${
                      isSelected
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    {kp.name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {filteredPoints.length === 0 && (
          <div className="text-center py-4 text-gray-400 text-xs">
            未找到匹配的知识点
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgePointSelector;
